import { allPageTypes } from "global/enums/allPageTypes"
import { gEnums } from "global/enums/globalEnums"
import { useContext, useMemo } from "react"
import { ParentContext } from "../ParentContext"
import { FrameworkContext } from "../FrameworkContent"
import { useAppUserAccess } from "./useAppUserAccess"
import { useGridItems } from "./useGridItems"

export const useAllowItemsMain = ({ gcProps, gridPageItems, hidden }) => {

  const parentContext = useContext(ParentContext)
  const { states, fns, settings } = parentContext ?? {}
  const { homeSettings } = settings
  const { currentSettings_state, app_state, paps_state, questionsAndAnswers_state } = states
  const { page_fns, storage_fns } = fns

  const gridItems = useGridItems({ viewItems: gridPageItems })

  const { questionsAndAnswersActive } = questionsAndAnswers_state ?? {}

  // papsContext 
  const { pathViews } = paps_state ?? {}

  // appUserContext  
  const appUserAccess = useAppUserAccess()
  const { appUserAccessType, appUserSessionKey, events: events_appUser } = appUserAccess ?? {}
  const currentAppUserEvent = events_appUser && pathViews.events ? events_appUser[pathViews.events] : null
  const { appUserKey } = currentAppUserEvent ? currentAppUserEvent : {}
  const _appUserSessionKey = appUserKey ? appUserKey : appUserSessionKey

  // frameworkContext   
  const { framework_state } = useContext(FrameworkContext) ?? {}
  const { desktopMode } = framework_state ?? {}

  const isMobileDevice = _.get(app_state, 'currentEnvironment.platform.isMobileDevice', false)

  // pageContext  
  const { aps_global, aps_appUserSettings, aps_viewItems } = _.get(currentSettings_state, 'gvviSettings', {});

  const { global: global_home } = homeSettings ?? {}
  const { appUrls: homeAppUrls } = global_home ?? {}
  const { appUrls: appUrlz } = homeAppUrls ?? {}

  const weatherLink = page_fns.getLink(appUrlz, 'weather', isMobileDevice)

  const { eventOptions, clientOptions, appGallery, appQuestionsAndAnswers } = aps_global ?? {}
  const { allowGallery, galleryCaption, galleryIcon } = appGallery ? appGallery : {}
  const { allowQuestionsAndAnswers, showOnLandingPage, landingPageLocation, qAndACaption, useIconImage: useIconImage_qa, iconImage: iconImage_qa } = appQuestionsAndAnswers ?? {}

  const _appOptions = { ...clientOptions, ...eventOptions }
  let { showLyft, showLyftOnHomeScreen, showUber, showUberOnHomeScreen, showWeatherOnHomeScreen, showWeather, weatherZip } = _appOptions ?? {}
  const { appUserCollection, showMyProfileLinkOnHomeScreen, myProfileCaption, myProfileIcon, myProfileIconLocation, allowNonEventAppUsers } = aps_appUserSettings ?? {}
  const { useIconImage, iconImage } = aps_appUserSettings

  const allowQandA = useMemo(() => {
    let _allowQandA = questionsAndAnswersActive && questionsAndAnswersActive.length > 0
    return allowQuestionsAndAnswers && showOnLandingPage && _allowQandA && landingPageLocation !== gEnums.landingPageLocations.appUser
  }, [allowQuestionsAndAnswers, showOnLandingPage, questionsAndAnswersActive, landingPageLocation])

  const _allowMyProfileIcon = useMemo(() => {
    if (!showMyProfileLinkOnHomeScreen) return false;
    switch (appUserAccessType) {
      case gEnums.appUserAccessTypes.appAdmin:
      case gEnums.appUserAccessTypes.appDataAdmin:
      case gEnums.appUserAccessTypes.appDataAdminLimited:
      case gEnums.appUserAccessTypes.appProfile:
      case gEnums.appUserAccessTypes.appSubAdmin:
        if (_appUserSessionKey || allowNonEventAppUsers) {
          return true
        }
        break;
      default:
      // nothing
    }
  }, [showMyProfileLinkOnHomeScreen, appUserAccessType, _appUserSessionKey, allowNonEventAppUsers])


  const allowedItem = (viewItem, segProps, gcProps) => {
    const { key } = viewItem ?? {}
    return { viewItem, itemKey: key, segProps, gcProps, hidden }
  }

  const myProfileGridItem = (items) => {

    const iconImageUrls = storage_fns.getIconUrl(useIconImage, iconImage)

    const giProps = {
      caption: myProfileCaption ? myProfileCaption : _.startCase('myProfile'),
      icon: myProfileIcon ? myProfileIcon : 'user',
      cloned: true,
      iconImageUrls,
    }

    if (appUserAccess && pathViews.events && appUserAccess.events && appUserAccess.events[pathViews.events] && appUserAccess.accessLevel > gEnums.accessLevels.guest.value) {
      switch (myProfileIconLocation) {
        case gEnums.iconLocations.last:
          items.push(allowedItem({ key: appUserCollection, itemKey: _appUserSessionKey }, giProps, gcProps))
          break;
        default:
          items.unshift(allowedItem({ key: appUserCollection, itemKey: _appUserSessionKey }, giProps, gcProps))
      }
    } else {
      items.unshift(allowedItem({ key: appUserCollection, itemKey: _appUserSessionKey }, giProps, gcProps))
    }
  }

  const items = useMemo(() => {
    const _items = []
    if (gridItems) {
      _.forEach(gridItems, (viewItem) => {
        const { key, segProps } = viewItem
        switch (key) {
          case 'events':
            if (!pathViews.events) {
              _items.push(allowedItem(viewItem, segProps, gcProps))
            }
            break;
          default:
            _items.push(allowedItem(viewItem, segProps, gcProps))
        }
      })
    }

    if (allowQandA) {
      const iconImageUrls_qa = storage_fns.getIconUrl(useIconImage_qa, iconImage_qa)
      if (landingPageLocation === gEnums.landingPageLocations.normalFirst) {
        _items.unshift(allowedItem({ key: allPageTypes.qAndA }, { caption: qAndACaption ? qAndACaption : 'Q&A', icon: 'question circle', iconImageUrls: iconImageUrls_qa }, gcProps))
      } else {
        _items.push(allowedItem({ key: allPageTypes.qAndA }, { caption: qAndACaption ? qAndACaption : 'Q&A', icon: 'question circle', iconImageUrls: iconImageUrls_qa }, gcProps))
      }
    }
    if (_allowMyProfileIcon) { myProfileGridItem(_items) }
    if (allowGallery) { _items.push(allowedItem({ key: 'gallery' }, { caption: galleryCaption ? galleryCaption : 'Gallery', icon: galleryIcon ? galleryIcon : 'images outline' }, gcProps)) }

    return _items
  }, [gridItems])

  const altItems = useMemo(() => {
    const _altItems = []
    if (showLyft && showLyftOnHomeScreen) { _altItems.push(allowedItem({ key: 'lyft' }, { caption: 'Lyft', icon: 'lyft' }, gcProps)) }
    if (showUber && showUberOnHomeScreen) { _altItems.push(allowedItem({ key: 'uber' }, { caption: 'Uber', icon: 'uber' }, gcProps)) }
    if (showWeather && showWeatherOnHomeScreen && weatherLink && weatherZip) { _altItems.push(allowedItem({ key: 'weather' }, { caption: 'Weather', icon: 'cloud' }, gcProps)) }
    return _altItems
  }, [showLyft, showLyftOnHomeScreen, showUber, showUberOnHomeScreen, showWeather, showWeatherOnHomeScreen, weatherLink, weatherZip])

  return [...items, ...altItems]

}